/**
 * EditorEventPanel: CRUD + reorder + test for a selected object's event actions.
 *
 * Mounted inside the property panel (only when the object has an `interaction`).
 * Renders one card per event in `interaction.events`: name, type, the type's
 * payload fields, and 测试 / ↑ / ↓ / × controls. An 事件类型 dropdown + 添加按钮
 * appends a new event.
 *
 * Like the property panel, text inputs write back to the live object on every
 * keystroke (via `mutate`) WITHOUT re-rendering, so focus is preserved; only
 * structural edits (add / delete / reorder / type change) re-render the list.
 * 测试 fires a one-off OBJECT_TRIGGER carrying just that event, so the designer
 * sees exactly what the player would.
 */

import type { SceneObject, SceneEventAction, TriggerEventType } from '../scene/SceneObjectTypes';
import type { EditorState } from './EditorState';
import type { SceneObjectManager } from '../scene/SceneObjectManager';
import { mintObjectId } from './EditorAssetLibrary';

export interface EventPanelMutators {
    mutate(id: string, fn: (o: SceneObject) => void): void;
    changed(): void;
    testEvent(id: string, eventIndex: number): void;
}

const EVENT_TYPES: { value: TriggerEventType; label: string }[] = [
    { value: 'showPanel', label: '显示面板' },
    { value: 'openUrl', label: '打开链接' },
    { value: 'playVideo', label: '播放视频' },
    { value: 'showImage', label: '显示图片' },
    { value: 'callApi', label: '调用接口' },
    { value: 'switchScene', label: '切换场景' },
    { value: 'hideScene', label: '隐藏场景' },
    { value: 'custom', label: '自定义' }
];

export class EditorEventPanel {
    private _mount: HTMLElement;
    private _state: EditorState;
    private _getMgr: () => SceneObjectManager | null;
    private _mut: EventPanelMutators;

    constructor(
        mount: HTMLElement,
        state: EditorState,
        getMgr: () => SceneObjectManager | null,
        mut: EventPanelMutators
    ) {
        this._mount = mount;
        this._state = state;
        this._getMgr = getMgr;
        this._mut = mut;
        this._render();
    }

    /** Re-render the event list (after a structural edit or selection change). */
    render(): void { this._render(); }

    private _render(): void {
        const id = this._state.selectedId;
        const o = id ? this._getMgr()?.get(id) : undefined;
        this._mount.innerHTML = '';

        if (!id || !o || !o.interaction) {
            const empty = document.createElement('div');
            empty.className = 'ep-empty';
            empty.textContent = '该对象无事件配置';
            this._mount.appendChild(empty);
            return;
        }

        const events = o.interaction.events;
        const list = document.createElement('div');
        list.className = 'ev-list';
        events.forEach((ev, i) => list.appendChild(this._card(o, ev, i, events.length)));
        this._mount.appendChild(list);

        this._mount.appendChild(this._addBar(o));
    }

    private _card(o: SceneObject, ev: SceneEventAction, i: number, total: number): HTMLElement {
        const card = document.createElement('div');
        card.className = 'ev-card';

        // Top row: name + type + actions.
        const top = document.createElement('div');
        top.className = 'ev-top';
        const name = document.createElement('input');
        name.type = 'text';
        name.className = 'ep-input ep-grow';
        name.value = ev.name;
        name.placeholder = '事件名称';
        name.addEventListener('input', () => {
            this._mut.mutate(o.id, (x) => { x.interaction!.events[i].name = name.value; });
        });
        top.appendChild(name);

        const type = document.createElement('select');
        type.className = 'ev-type';
        for (const t of EVENT_TYPES) {
            const opt = document.createElement('option');
            opt.value = t.value; opt.textContent = t.label;
            if (t.value === ev.type) opt.selected = true;
            type.appendChild(opt);
        }
        type.addEventListener('change', () => {
            const newType = type.value as TriggerEventType;
            this._mut.mutate(o.id, (x) => {
                x.interaction!.events[i].type = newType;
                x.interaction!.events[i].payload = defaultPayload(newType);
            });
            this._mut.changed();
            this._render();
        });
        top.appendChild(type);

        top.appendChild(this._iconBtn('↑', '上移', () => this._move(o.id, i, -1), i === 0));
        top.appendChild(this._iconBtn('↓', '下移', () => this._move(o.id, i, 1), i === total - 1));
        top.appendChild(this._iconBtn('▶', '测试此事件', () => this._mut.testEvent(o.id, i), false, 'ev-test'));
        top.appendChild(this._iconBtn('×', '删除', () => this._remove(o.id, i), false, 'ev-del'));
        card.appendChild(top);

        // Type-specific payload form.
        card.appendChild(this._form(o.id, ev, i));
        return card;
    }

    /** The per-type payload editor. Text fields mutate without re-render. */
    private _form(id: string, ev: SceneEventAction, i: number): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'ev-form';
        const p = ev.payload;
        const setField = (key: keyof typeof p, value: unknown) => {
            this._mut.mutate(id, (x) => { (x.interaction!.events[i].payload as Record<string, unknown>)[key as string] = value; });
        };

        // 通用选项：只触发一次（本场景内每人一次，重进场景后重置）。
        const once = document.createElement('input');
        once.type = 'checkbox';
        once.className = 'ev-once';
        once.checked = Boolean(ev.triggerOnce);
        once.title = '勾选后每位玩家在本次进入场景期间只触发一次，重新进入场景后重置';
        once.addEventListener('change', () => {
            this._mut.mutate(id, (x) => { x.interaction!.events[i].triggerOnce = once.checked; });
        });
        wrap.appendChild(this._row('只触发一次', once));

        switch (ev.type) {
            case 'showPanel':
                wrap.appendChild(this._row('标题', this._text(p.title ?? '', (v) => setField('title', v))));
                wrap.appendChild(this._row('内容', this._area(p.content ?? '', (v) => setField('content', v)), true));
                break;
            case 'openUrl':
                wrap.appendChild(this._row('链接 URL', this._text(p.url ?? '', (v) => setField('url', v))));
                break;
            case 'playVideo':
                wrap.appendChild(this._row('视频 URL', this._text(p.videoUrl ?? p.url ?? '', (v) => setField('videoUrl', v))));
                wrap.appendChild(this._row('标题', this._text(p.title ?? '', (v) => setField('title', v))));
                break;
            case 'showImage':
                wrap.appendChild(this._row('图片 URL', this._text(p.imageUrl ?? p.url ?? '', (v) => setField('imageUrl', v))));
                wrap.appendChild(this._row('标题', this._text(p.title ?? '', (v) => setField('title', v))));
                break;
            case 'callApi':
                wrap.appendChild(this._row('接口 URL', this._text(p.apiUrl ?? '', (v) => setField('apiUrl', v))));
                wrap.appendChild(this._selectRow('方法', [['GET', 'GET'], ['POST', 'POST']], p.method ?? 'GET', (v) => setField('method', v as 'GET' | 'POST')));
                wrap.appendChild(this._selectRow('成功后', [['none', '无'], ['showPanel', '显示面板'], ['openUrl', '打开链接']], p.onSuccess ?? 'none', (v) => setField('onSuccess', v)));
                break;
            case 'switchScene':
                wrap.appendChild(this._row('目标场景', this._text(p.targetScene ?? '', (v) => setField('targetScene', v))));
                break;
            case 'hideScene': {
                const hint = document.createElement('div');
                hint.className = 'ev-hint';
                hint.textContent = '进入触发区域时隐藏高斯模型、传送门等场景内容（保留天空盒、场景碰撞体与编辑器碰撞体）；离开区域时自动恢复。';
                wrap.appendChild(hint);
                break;
            }
            case 'custom':
                wrap.appendChild(this._row('自定义事件名', this._text(p.customEventName ?? '', (v) => setField('customEventName', v))));
                break;
        }
        return wrap;
    }

    private _addBar(o: SceneObject): HTMLElement {
        const bar = document.createElement('div');
        bar.className = 'ev-add';
        const sel = document.createElement('select');
        sel.className = 'ev-type';
        for (const t of EVENT_TYPES) {
            const opt = document.createElement('option');
            opt.value = t.value; opt.textContent = `+ ${t.label}`;
            sel.appendChild(opt);
        }
        bar.appendChild(sel);
        const add = document.createElement('button');
        add.className = 'etb-btn ev-add-btn';
        add.textContent = '添加事件';
        add.addEventListener('click', () => {
            const t = sel.value as TriggerEventType;
            this._mut.mutate(o.id, (x) => {
                x.interaction!.events.push({
                    id: mintObjectId('evt'),
                    type: t,
                    name: EVENT_TYPES.find((e) => e.value === t)?.label ?? t,
                    payload: defaultPayload(t)
                });
            });
            this._mut.changed();
            this._render();
        });
        bar.appendChild(add);
        return bar;
    }

    private _move(id: string, i: number, dir: number): void {
        this._mut.mutate(id, (x) => {
            const arr = x.interaction!.events;
            const j = i + dir;
            if (j < 0 || j >= arr.length) return;
            [arr[i], arr[j]] = [arr[j], arr[i]];
        });
        this._mut.changed();
        this._render();
    }

    private _remove(id: string, i: number): void {
        this._mut.mutate(id, (x) => { x.interaction!.events.splice(i, 1); });
        this._mut.changed();
        this._render();
    }

    // ── DOM helpers ──
    /** 事件行：默认标签居左、控件居右；stacked 时纵向（多行文本域等）。 */
    private _row(label: string, field: HTMLElement, stacked = false): HTMLElement {
        const r = document.createElement('div');
        r.className = stacked ? 'ev-row ev-col' : 'ev-row';
        const l = document.createElement('span');
        l.className = 'ep-label'; l.textContent = label;
        r.appendChild(l); r.appendChild(field);
        return r;
    }

    private _text(value: string, on: (v: string) => void): HTMLInputElement {
        const i = document.createElement('input');
        i.type = 'text'; i.value = value; i.className = 'ep-input ep-grow';
        i.addEventListener('input', () => on(i.value));
        return i;
    }

    private _area(value: string, on: (v: string) => void): HTMLTextAreaElement {
        const i = document.createElement('textarea');
        i.value = value; i.className = 'ep-input ep-grow ev-area'; i.rows = 2;
        i.addEventListener('input', () => on(i.value));
        return i;
    }

    private _selectRow(label: string, options: [string, string][], current: string, on: (v: string) => void): HTMLElement {
        const sel = document.createElement('select');
        sel.className = 'ev-type';
        for (const [v, lbl] of options) {
            const opt = document.createElement('option');
            opt.value = v; opt.textContent = lbl;
            if (v === current) opt.selected = true;
            sel.appendChild(opt);
        }
        sel.addEventListener('change', () => on(sel.value));
        return this._row(label, sel);
    }

    private _iconBtn(label: string, title: string, on: () => void, disabled: boolean, cls = ''): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = `ev-icon ${cls}`.trim();
        b.textContent = label; b.title = title; b.disabled = disabled;
        b.addEventListener('click', on);
        return b;
    }

    destroy(): void { this._mount.innerHTML = ''; }
}

/** Sensible empty payload for a freshly created / retyped event. */
function defaultPayload(type: TriggerEventType): SceneEventAction['payload'] {
    switch (type) {
        case 'showPanel': return { title: '标题', content: '内容' };
        case 'openUrl': return { url: 'https://' };
        case 'playVideo': return { videoUrl: '', title: '' };
        case 'showImage': return { imageUrl: '', title: '' };
        case 'callApi': return { apiUrl: '', method: 'GET', onSuccess: 'none' };
        case 'switchScene': return { targetScene: '' };
        case 'hideScene': return {};
        case 'custom': return { customEventName: '' };
    }
}
